/**
 * SEO Component - Centralized meta tag management
 * Provides fallback meta tags, dynamic updates, and structured data for all pages
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { productUrl } from '../utils/slug';

// Site-wide defaults
const SITE_NAME = 'North Cyprus Philately';
const DEFAULT_TITLE = 'North Cyprus Philately – Rare Stamps, Year Sets & First Day Covers from TRNC';
const DEFAULT_DESCRIPTION = 'Discover rare stamps, year sets, first day covers, and postal history from Turkish Republic of Northern Cyprus (TRNC). Expert philatelic service since 1974.';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_philately-shop/artifacts/81ter58o_77df6711-591a-4e86-9122-0f8adb5c4777.png';
const SITE_URL = 'https://www.northcyprusphilately.com';
const SITE_LOGO = 'https://customer-assets.emergentagent.com/job_philately-shop/artifacts/81ter58o_77df6711-591a-4e86-9122-0f8adb5c4777.png';

// Language to locale mapping for og:locale
const LOCALE_MAP = {
  en: 'en_US',
  tr: 'tr_TR',
  el: 'el_GR',
  de: 'de_DE'
};

// Tracking params to remove from canonical URLs
const TRACKING_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'fbclid', 'gclid', 'ref', 'source', '_ga', '_gl'];

/**
 * Remove tracking parameters from URL for canonical
 */
const getCanonicalUrl = (pathname, search) => {
  const baseUrl = SITE_URL + pathname;
  
  if (!search) return baseUrl;
  
  const params = new URLSearchParams(search);
  TRACKING_PARAMS.forEach(param => params.delete(param));
  
  const cleanSearch = params.toString();
  return cleanSearch ? `${baseUrl}?${cleanSearch}` : baseUrl;
};

/**
 * Check if running on preview/staging environment
 */
const isPreviewEnvironment = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host.includes('preview.emergentagent.com') || 
         host.includes('localhost') || 
         host.includes('127.0.0.1');
};

/**
 * Main SEO Component
 */
const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = 'North Cyprus stamps, TRNC philately, rare stamps, first day covers, year sets, stamp collecting, Turkish Cyprus postal history, KKTC stamps, collectible stamps',
  image = DEFAULT_IMAGE,
  imageAlt = '',
  url,
  type = 'website',
  article = false,
  author = 'North Cyprus Philately',
  noindex = false,
  structuredData = null,
  lang = 'en'
}) => {
  const location = useLocation();

  // Build canonical URL (clean of tracking params)
  const canonicalUrl = url || getCanonicalUrl(location.pathname, location.search);
  const currentLocale = LOCALE_MAP[lang] || LOCALE_MAP.en;
  
  // Build title
  const fullTitle = title 
    ? (title === SITE_NAME ? title : `${title} | ${SITE_NAME}`)
    : DEFAULT_TITLE;
  
  // Force noindex on preview environments
  const shouldNoindex = noindex || isPreviewEnvironment();

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description || DEFAULT_DESCRIPTION} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots - noindex for preview environments */}
      {shouldNoindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <>
          <meta name="robots" content="index, follow" />
          <meta name="googlebot" content="index, follow" />
        </>
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || DEFAULT_DESCRIPTION} />
      <meta property="og:image" content={image || DEFAULT_IMAGE} />
      <meta property="og:image:alt" content={imageAlt || title || SITE_NAME} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={currentLocale} />
      {Object.entries(LOCALE_MAP).filter(([k]) => k !== lang).map(([, locale]) => (
        <meta key={locale} property="og:locale:alternate" content={locale} />
      ))}
      
      {/* Article specific tags */}
      {article && article.publishedTime && (
        <>
          <meta property="article:author" content={author} />
          <meta property="article:published_time" content={article.publishedTime} />
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
        </>
      )}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description || DEFAULT_DESCRIPTION} />
      <meta name="twitter:image" content={image || DEFAULT_IMAGE} />
      <meta name="twitter:image:alt" content={imageAlt || title || SITE_NAME} />
      
      {/* Hreflang tags for multilingual support - same URL serves all languages via client-side switching */}
      <link rel="alternate" hrefLang="en" href={canonicalUrl} />
      <link rel="alternate" hrefLang="tr" href={canonicalUrl} />
      <link rel="alternate" hrefLang="el" href={canonicalUrl} />
      <link rel="alternate" hrefLang="de" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
      
      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

/**
 * Product Page SEO with structured data
 */
export const ProductSEO = ({ product }) => {
  const location = useLocation();
  
  if (!product) {
    return <SEO title="Loading Product..." />;
  }
  
  const title = [
    product.title || product.name,
    product.sku ? `(${product.sku})` : '',
    product.condition ? `- ${product.condition}` : ''
  ].filter(Boolean).join(' ');
  
  const description = product.description 
    ? product.description.substring(0, 155) + '...'
    : `${product.title || product.name} - Rare philatelic item from North Cyprus Philately. ${product.condition || ''} condition. Shop now!`;
  
  const image = product.images?.[0] || product.image || DEFAULT_IMAGE;
  const canonicalUrl = getCanonicalUrl(location.pathname, '');
  
  // Product structured data (Schema.org)
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.title || product.name,
    "description": description,
    "image": {
      "@type": "ImageObject",
      "url": image,
      "width": 600,
      "height": 600
    },
    "sku": product.sku || product.id,
    "brand": {
      "@type": "Brand",
      "name": "North Cyprus Philately"
    },
    "offers": {
      "@type": "Offer",
      "price": product.price || 0,
      "priceCurrency": (product.currency || "EUR").toUpperCase(),
      "availability": (product.stock === undefined || product.stock > 0)
        ? "https://schema.org/InStock" 
        : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": "North Cyprus Philately"
      },
      "url": canonicalUrl
    }
  };
  
  // Add condition if available
  if (product.condition) {
    productSchema.itemCondition = product.condition.toLowerCase().includes('mint') 
      ? "https://schema.org/NewCondition"
      : "https://schema.org/UsedCondition";
  }
  
  // Breadcrumb structured data
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": SITE_URL
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Shop",
        "item": `${SITE_URL}/shop`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": product.title || product.name,
        "item": canonicalUrl
      }
    ]
  };
  
  return (
    <SEO
      title={title}
      description={description}
      image={image}
      type="product"
      url={canonicalUrl}
      structuredData={[productSchema, breadcrumbSchema]}
    />
  );
};

/**
 * Category/Shop Page SEO
 */
export const CategorySEO = ({ category, productCount }) => {
  const location = useLocation();
  const canonicalUrl = getCanonicalUrl(location.pathname, location.search);
  
  if (!category) {
    return (
      <SEO 
        title="Shop - Rare Stamps & Postal History"
        description="Browse our collection of rare stamps, year sets, first day covers, and postal history from Turkish Republic of Northern Cyprus (TRNC)."
        url={canonicalUrl}
      />
    );
  }
  
  const title = `${category.name} - TRNC Stamps & Covers`;
  const description = category.description 
    ? category.description.substring(0, 155) + '...'
    : `Browse ${productCount || 'our'} ${category.name} items. Rare philatelic collectibles from North Cyprus Philately.`;
  
  // Breadcrumb for category
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": SITE_URL
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Shop",
        "item": `${SITE_URL}/shop`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": category.name,
        "item": canonicalUrl
      }
    ]
  };
  
  return (
    <SEO
      title={title}
      description={description}
      url={canonicalUrl}
      structuredData={breadcrumbSchema}
    />
  );
};

/**
 * Blog Article SEO
 */
export const BlogSEO = ({ article }) => {
  const location = useLocation();
  const canonicalUrl = getCanonicalUrl(location.pathname, '');
  
  if (!article) {
    return (
      <SEO 
        title="Blog - Philatelic News & Articles"
        description="Read the latest news, articles, and guides about stamp collecting, postal history, and TRNC philately."
        url={canonicalUrl}
      />
    );
  }
  
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": article.excerpt || article.description,
    "image": article.image || DEFAULT_IMAGE,
    "datePublished": article.created_at,
    "dateModified": article.updated_at || article.created_at,
    "author": {
      "@type": "Organization",
      "name": "North Cyprus Philately"
    },
    "publisher": {
      "@type": "Organization",
      "name": "North Cyprus Philately",
      "logo": {
        "@type": "ImageObject",
        "url": SITE_LOGO
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    }
  };
  
  return (
    <SEO
      title={article.title}
      description={article.excerpt || article.description?.substring(0, 155)}
      image={article.image}
      type="article"
      url={canonicalUrl}
      article={{
        publishedTime: article.created_at,
        modifiedTime: article.updated_at
      }}
      structuredData={articleSchema}
    />
  );
};

/**
 * Organization structured data (for homepage)
 */
export const OrganizationSchema = () => {
  // Combined Organization + LocalBusiness (Store) schema for maximum SEO coverage
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": ["Organization", "Store"],
    "name": "North Cyprus Philately",
    "alternateName": "NCP Stamps",
    "url": SITE_URL,
    "logo": {
      "@type": "ImageObject",
      "url": SITE_LOGO,
      "width": 512,
      "height": 512
    },
    "image": SITE_LOGO,
    "description": DEFAULT_DESCRIPTION,
    "foundingDate": "1994",
    "slogan": "Collecting History, One Stamp at a Time",
    "sameAs": [
      "https://www.ebay.com/usr/north_cyprus_philately",
      "https://www.delcampe.net/en_GB/collectables/shop/NorthCyprusPhilately"
    ],
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Lefkoşa",
      "addressRegion": "North Cyprus",
      "addressCountry": "CY"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "35.185",
      "longitude": "33.382"
    },
    "areaServed": [
      { "@type": "Country", "name": "United Kingdom" },
      { "@type": "Country", "name": "Germany" },
      { "@type": "Country", "name": "Greece" },
      { "@type": "Country", "name": "Turkey" },
      { "@type": "Country", "name": "United States" },
      { "@type": "Country", "name": "Cyprus" }
    ],
    "priceRange": "$2 - $500",
    "currenciesAccepted": "USD, EUR, GBP",
    "paymentAccepted": "Credit Card, PayPal, Bank Transfer, Cryptocurrency",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "10:00",
        "closes": "14:00"
      }
    ],
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "email": "northcyprusphilately@gmail.com",
        "availableLanguage": ["English", "Turkish", "Greek", "German"]
      },
      {
        "@type": "ContactPoint",
        "contactType": "sales",
        "email": "northcyprusphilately@gmail.com",
        "url": `${SITE_URL}/contact`
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "North Cyprus Stamps Collection",
      "itemListElement": [
        { "@type": "OfferCatalog", "name": "Stamps" },
        { "@type": "OfferCatalog", "name": "First Day Covers" },
        { "@type": "OfferCatalog", "name": "Year Sets" },
        { "@type": "OfferCatalog", "name": "Postal History" }
      ]
    },
    "makesOffer": {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Product",
        "name": "Philatelic Stamps & Covers"
      }
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
    </Helmet>
  );
};

/**
 * WebSite schema with SearchAction for Google sitelinks search box
 */
export const WebSiteSchema = () => {
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": SITE_NAME,
    "url": SITE_URL,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${SITE_URL}/shop?search={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
    </Helmet>
  );
};

/**
 * FAQ Page structured data for rich snippets in Google
 */
export const FAQSchema = ({ faqs }) => {
  if (!faqs || faqs.length === 0) return null;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
    </Helmet>
  );
};

/**
 * CollectionPage + ItemList Schema for Shop/Category pages
 * Enables rich product carousels and collection snippets in Google
 */
export const CollectionPageSchema = ({ products, categoryName, currentPage, totalItems, pageUrl }) => {
  if (!products || products.length === 0) return null;

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": categoryName ? `${categoryName} - North Cyprus Philately` : "Shop - North Cyprus Philately",
    "description": categoryName
      ? `Browse ${totalItems || products.length} ${categoryName} items. Rare philatelic collectibles from North Cyprus.`
      : `Browse ${totalItems || products.length} rare stamps, year sets, and first day covers from North Cyprus Philately.`,
    "url": pageUrl || `${SITE_URL}/shop`,
    "numberOfItems": totalItems || products.length,
    "provider": {
      "@type": "Organization",
      "name": "North Cyprus Philately",
      "url": SITE_URL
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": products.slice(0, 30).map((product, index) => {
        const productPrice = product.discounted_price && product.discounted_price < product.price
          ? product.discounted_price
          : product.price;
        const productImage = product.images?.[0]
          ? (product.images[0].startsWith('http') ? product.images[0] : `${SITE_URL}${product.images[0]}`)
          : DEFAULT_IMAGE;

        return {
          "@type": "ListItem",
          "position": ((currentPage || 1) - 1) * products.length + index + 1,
          "item": {
            "@type": "Product",
            "name": product.title,
            "url": `${SITE_URL}${productUrl(product)}`,
            "image": productImage,
            "description": (product.description || product.title || '').substring(0, 150),
            "sku": product.sku || product.id,
            "offers": {
              "@type": "Offer",
              "price": productPrice,
              "priceCurrency": product.currency || "USD",
              "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
              "seller": {
                "@type": "Organization",
                "name": "North Cyprus Philately"
              }
            },
            ...(product.condition ? {
              "itemCondition": product.condition.toLowerCase().includes('mint')
                ? "https://schema.org/NewCondition"
                : "https://schema.org/UsedCondition"
            } : {})
          }
        };
      })
    }
  };

  // Breadcrumb for shop/category
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
      { "@type": "ListItem", "position": 2, "name": "Shop", "item": `${SITE_URL}/shop` },
      ...(categoryName ? [{ "@type": "ListItem", "position": 3, "name": categoryName }] : [])
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(collectionSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
};

export default SEO;
